import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { Transaction } from '../../../types/dashboard';

export function useValidationData() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agencies, setAgencies] = useState<{ name: string; company: string }[]>([]);
  const [negotiators, setNegotiators] = useState<string[]>([]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Charger les agences
      const { data: agenciesData } = await supabase
        .from('agencies')
        .select('name, company')
        .eq('active', true)
        .order('company')
        .order('name');
      
      setAgencies(agenciesData || []);

      // Charger les négociateurs
      const { data: negotiatorsData } = await supabase
        .from('negotiators')
        .select('id, firstname, lastname')
        .order('lastname');
      
      setNegotiators(negotiatorsData?.map(n => `${n.firstname} ${n.lastname}`) || []);

      // Charger les transactions avec les champs de validation
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select(`
          id,
          property_number,
          seller,
          buyer,
          sale_price,
          fees_with_tax,
          fees_without_tax,
          mandate_type,
          property_origin,
          copro_id,
          copros (
            numero,
            residence
          ),
          status,
          compromise_date,
          expected_acte_date,
          regul,
          commentaire_regul,
          regul_requests!left (
            request_comment,
            status,
            created_at
          ),
          acte_date,
          invoice_date,
          invoice_number,
          cancelled_at,
          cancellation_date,
          cancellation_reason,
          approved,
          approved_by,
          approved_at,
          encaissement_date,
          cancelled_at,
          cancellation_date,
          cancellation_reason,
          approved_by:approved_by (
            firstname,
            lastname
          ),
          transaction_agencies (
            agency
          ),
          transaction_negotiators!left (
            negotiator:negotiators!inner (
              id,
              firstname,
              lastname
            ),
            role,
            percentage
          ),
          transaction_assistants!left (
            assistant:assistants!inner (
              id,
              firstname,
              lastname
            ),
            role
          ),
          transaction_retrocessions!left (
            id,
            type,
            amount
          )
        `);

      if (transactionsError) throw transactionsError;

      const formattedTransactions = (transactionsData || []).map(t => ({
        ...t,
        agencies: t.transaction_agencies.map(ta => ta.agency),
        negotiators: t.transaction_negotiators?.map(tn => ({
          id: tn.negotiator.id,
          name: `${tn.negotiator.firstname} ${tn.negotiator.lastname}`,
          role: tn.role,
          percentage: tn.percentage
        })) || [],
        assistants: t.transaction_assistants?.map(ta => ({
          id: ta.assistant.id,
          name: `${ta.assistant.firstname} ${ta.assistant.lastname}`,
          role: ta.role
        })) || [],
        retrocessions: t.transaction_retrocessions?.map(tr => ({
          id: tr.id,
          type: tr.type,
          amount: tr.amount
        })) || [],
        regul_comment: t.regul_requests && t.regul_requests.length > 0 
          ? t.regul_requests
              .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]
              .request_comment
          : null,
        approved_by_name: t.approved_by ? `${t.approved_by.firstname} ${t.approved_by.lastname}` : null
      }));

      // Debug: Log des champs d'annulation pour quelques transactions
      console.log('🔍 DEBUG: Champs d\'annulation pour les 3 premières transactions:');
      formattedTransactions.slice(0, 3).forEach(t => {
        console.log(`Transaction ${t.property_number}:`, {
          cancelled_at: t.cancelled_at,
          cancellation_date: t.cancellation_date,
          cancellation_reason: t.cancellation_reason,
          cancellation_reason_type: typeof t.cancellation_reason,
          cancellation_reason_length: t.cancellation_reason ? t.cancellation_reason.length : 'null/undefined',
          status: t.status
        });
      });

      // Debug: Log des champs de régularisation pour les 5 premières transactions
      console.log('🔍 DEBUG: Champs de régularisation pour les 5 premières transactions:');
      formattedTransactions.slice(0, 5).forEach(t => {
        console.log(`Transaction ${t.property_number}:`, {
          regul: t.regul,
          regul_type: typeof t.regul,
          commentaire_regul: t.commentaire_regul,
          commentaire_regul_type: typeof t.commentaire_regul,
          commentaire_regul_length: t.commentaire_regul ? t.commentaire_regul.length : 'null/undefined',
          regul_status: t.regul_status,
          status: t.status
        });
      });

      console.log('Loaded transactions with validation data:', formattedTransactions.map(t => ({
        property_number: t.property_number,
        property_origin: t.property_origin,
        copro_id: t.copro_id,
        copro_details: t.copros
      })));

      setTransactions(formattedTransactions);
      setFilteredTransactions(formattedTransactions);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return {
    transactions,
    filteredTransactions,
    setFilteredTransactions,
    loading,
    error,
    agencies,
    negotiators,
    loadData
  };
}